import React, { createContext, useContext, useEffect, ReactNode } from 'react'
import { Session, User } from '@supabase/supabase-js'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { setAuthState, initializeAuth } from '../features/auth/lib/slice'
import type { AuthError } from '../features/auth/lib/slice'
import { AuthService, UserProfile } from '../services/AuthService'

interface AuthContextType {
  user: User | null
  profile: UserProfile | null // Agregado
  session: Session | null
  isAuthenticated: boolean
  isLoading: boolean
  error: AuthError | null
  isInitialized: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

const LoadUserProfileWithDelay = async (authService: AuthService): Promise<any> => {
  return new Promise((resolve) => {
    setTimeout(async () => {
      const { user, profile } = await authService.getCurrentUserWithProfile()
      resolve({ user, profile })
    }, 1000)
  })
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch()
  const authState = useAppSelector((state) => state.auth)
  const authService = AuthService.getInstance()

  useEffect(() => {
    dispatch(initializeAuth())

    const { data: { subscription } } = authService.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session)

        if (session?.user) {
          LoadUserProfileWithDelay(authService).then(({ user, profile }) => {
            dispatch(setAuthState({
              user,
              session,
              profile,
            }))
          })
        } else {
          dispatch(setAuthState({
            user: null,
            session: null,
            profile: null,
          }))
        }
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [dispatch, authService])


  const contextValue: AuthContextType = {
    user: authState.user,
    profile: authState.profile, // Agregado
    session: authState.session,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    error: authState.error,
    isInitialized: authState.isInitialized,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}